import { FC, useCallback, useEffect, useMemo } from "react";
import {
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import {
  RowClassParams,
  RowSelectionOptions,
  SelectionColumnDef,
} from "ag-grid-community/dist/types/src/entities/gridOptions";
import { CustomCellRendererProps } from "ag-grid-react";
import { grey } from "@mui/material/colors";
import { useWillUnmount } from "rooks";
import { ResourcesDetailGrid } from "./ResourcesDetailGrid";
import { AgDataGrid, AgDataGridProps } from "../../../ag-grid/AgDataGrid";
import { getColumnDefByResourceType } from "../column-defs/getColumnDefByResourceType";
import { resourceGridExpanderColumnDef } from "../column-defs/grid-components/resourceGridExpanderColumnDef";
import { ResourceGridDefaultCellRenderer } from "../column-defs/column-def-cell-renderers/ResourceGridDefaultCellRenderer";
import {
  getResourceGridSize,
  resourceDetailGridMaxSize,
  resourceGridRowHeight,
} from "../../utils/constants/sizes";
import {
  notActionableResourceTypes,
  notSelectableResourceTypes,
} from "../../utils/constants/resource";
import type { RowHeightParams } from "ag-grid-community/dist/types/src/interfaces/iCallbackParams";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { setGridResourceSelectionByGridId } from "@/store/resources-grid/resourcesGridSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ResourceType } from "@/services/cloudchipr.api";
import { getResourceGridResourceInfoWithActionsColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/grid-components/getResourceGridResourceInfoWithActionsColumnDef";
import { gridDetailRowsHeightsSelector } from "@/store/resources-grid/selectors/grid/gridDetailRowsHeightsSelector";
import { ConfigToRememberStateInStorage } from "@/components/pages/common/ag-grid/utils/types/gridProps";

export interface ResourcesAgGridConfigs {
  hideSelection?: boolean;
  hideExpand?: boolean;
  hideRowActions?: boolean;
  preventDetailsDrawerOpener?: boolean;
}

interface ResourcesMainAgGridProps
  extends AgDataGridProps,
    ResourcesAgGridConfigs {
  resourceType: ResourceType;
  masterResourceType: ResourceType;
  masterGridId: string;
  parentRowId: string;
  api: GridApi | null;
}

export const ResourcesAgGrid: FC<ResourcesMainAgGridProps> = ({
  resourceType,
  masterGridId,
  parentRowId,
  api,
  masterResourceType,
  hideSelection,
  hideRowActions,
  hideExpand,
  preventDetailsDrawerOpener,
  onGridReady,
  ...props
}) => {
  const dispatch = useAppDispatch();

  const detailRowsHeights = useAppSelector((state) =>
    gridDetailRowsHeightsSelector(state, masterGridId),
  );

  const hasChildren = !!props.rowData?.at(0)?.child_resource;

  const context = useMemo(() => {
    return { masterGridId, preventDetailsDrawerOpener };
  }, [masterGridId, preventDetailsDrawerOpener]);

  const configToRememberStateInStorage: ConfigToRememberStateInStorage =
    useMemo(() => {
      return { uniqueNameAsKey: `${resourceType}-live-resources` };
    }, [resourceType]);

  const columnDefs = useMemo(() => {
    const cols = getColumnDefByResourceType(resourceType) ?? [];

    const columnDefs = [...cols];

    if (hasChildren && !hideExpand) {
      columnDefs.unshift(resourceGridExpanderColumnDef);
    }

    if (!notActionableResourceTypes.has(resourceType) && !hideRowActions) {
      columnDefs.push(
        getResourceGridResourceInfoWithActionsColumnDef(masterGridId),
      );
    }

    return columnDefs;
  }, [hideExpand, hideRowActions, resourceType, masterGridId, hasChildren]);

  const getRowHeight = useCallback(
    (params: RowHeightParams) => {
      const isDetailRow = params.node.detail;

      if (!isDetailRow) {
        return resourceGridRowHeight;
      }

      const childrenCount = params.data?.child_resource?.resources?.length ?? 0;

      let height = getResourceGridSize(childrenCount);

      const nodeId = params.node.id;

      if (nodeId) {
        const expandedChildrenHeight = detailRowsHeights?.[nodeId] ?? 0;
        height = height + expandedChildrenHeight;
      }

      return Math.min(height, resourceDetailGridMaxSize);
    },
    [detailRowsHeights],
  );

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedResourceIds = api
        .getSelectedRows()
        .map((resource) => resource.id);

      dispatch(
        setGridResourceSelectionByGridId({
          resourceType,
          parentRowId,
          gridId: masterGridId,
          selection: selectedResourceIds,
        }),
      );
    },
    [dispatch, resourceType, parentRowId, masterGridId],
  );

  const detailCellRenderer = useCallback(
    (props: CustomCellRendererProps<AllTypeResourcesData>) => {
      return (
        <ResourcesDetailGrid
          {...props}
          masterResourceType={masterResourceType}
          masterGridId={masterGridId}
        />
      );
    },
    [masterGridId, masterResourceType],
  );

  const onGridReadyHandler = useCallback(
    (event: GridReadyEvent) => {
      if (onGridReady) {
        onGridReady(event);
      }

      setTimeout(event.api.refreshHeader, 0);
    },
    [onGridReady],
  );

  const selectionEnabled =
    !notSelectableResourceTypes.has(resourceType) && !hideSelection;

  useEffect(() => {
    if (Object.keys(detailRowsHeights ?? {})?.length) {
      api?.resetRowHeights();
    }
  }, [api, detailRowsHeights]);

  useWillUnmount(() => {
    dispatch(
      setGridResourceSelectionByGridId({
        resourceType,
        parentRowId,
        gridId: masterGridId,
        selection: [],
      }),
    );
  });

  return (
    <AgDataGrid
      loading={false}
      context={context}
      columnDefs={columnDefs}
      masterDetail={hasChildren}
      getRowHeight={getRowHeight}
      defaultColDef={defaultColDef}
      headerHeight={resourceGridRowHeight}
      onSelectionChanged={onSelectionChanged}
      selectionColumnDef={selectionColumnDef}
      detailCellRenderer={detailCellRenderer}
      rowSelection={selectionEnabled ? rowSelection : undefined}
      configToRememberStateInStorage={configToRememberStateInStorage}
      getRowStyle={getRowStyle}
      onGridReady={onGridReadyHandler}
      {...props}
    />
  );
};

const getRowStyle = ({ data }: RowClassParams) => {
  return data?.resource?.is_protected
    ? { backgroundColor: grey[50] }
    : undefined;
};

const rowSelection: RowSelectionOptions = {
  mode: "multiRow",
  selectAll: "filtered",
  checkboxLocation: "selectionColumn",
  groupSelects: "filteredDescendants",
  isRowSelectable: (row) =>
    !row.group && !row.data?.resource?.marked_for_deletion,
};

const defaultColDef = {
  flex: 1,
  minWidth: 150,
  unSortIcon: true,
  cellRenderer: ResourceGridDefaultCellRenderer,
};

const selectionColumnDef: SelectionColumnDef = {
  width: 40,
  maxWidth: 40,
  pinned: "left",
  lockPinned: true,
};
